import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Award, 
  Heart, 
  Shield, 
  Clock, 
  Target,
  CheckCircle,
  Calendar,
  MapPin,
  Building
} from 'lucide-react';
import { Card, Button } from '../../components/ui';
import { mockDoctors } from '../../data/mockData';

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: Heart,
      title: 'Compassionate Care',
      description: 'We treat every patient with empathy, respect, and understanding, ensuring they feel heard and cared for throughout their healthcare journey.'
    },
    {
      icon: Shield,
      title: 'Excellence in Medicine',
      description: 'We maintain the highest standards of medical practice, continuously updating our knowledge and skills to provide the best possible care.'
    },
    {
      icon: Users,
      title: 'Patient-Centered Approach',
      description: 'Our patients are at the heart of everything we do. We tailor our services to meet individual needs and preferences.'
    },
    {
      icon: Target,
      title: 'Innovation & Technology',
      description: 'We embrace cutting-edge medical technology and innovative treatment methods to improve patient outcomes and experiences.'
    }
  ];

  const achievements = [
    'Joint Commission Accredited Medical Center',
    'Top 100 Hospitals for Clinical Excellence',
    'Centers of Excellence in Cardiology',
    'Magnet Recognition for Nursing Excellence',
    'Patient Safety Excellence Award',
    'Outstanding Achievement in Quality'
  ];

  const facilities = [
    {
      title: 'Advanced Diagnostic Center',
      description: 'State-of-the-art imaging and laboratory services including MRI, CT, ultrasound, and comprehensive lab testing.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=300&fit=crop'
    },
    {
      title: 'Emergency Department',
      description: '24/7 emergency care with trauma-certified physicians and rapid response capabilities for critical situations.',
      image: 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=400&h=300&fit=crop'
    },
    {
      title: 'Surgical Suites',
      description: 'Modern operating rooms equipped with the latest surgical technology for minimally invasive procedures.',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop'
    }
  ];

  const teamStats = [
    { number: '50+', label: 'Medical Professionals' },
    { number: '15+', label: 'Specialties' },
    { number: '20+', label: 'Years of Experience' },
    { number: '10K+', label: 'Patients Served' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920&h=1080&fit=crop&crop=center"
            alt="About Our Medical Team"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/60 via-blue-900/70 to-blue-800/65"></div>
        </div>
        
        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full opacity-10 blur-3xl transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 translate-y-32"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center space-y-8">
            {/* About Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
                About ClinicIT Medical Center
              </span>
            </div>
            
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                About{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  ClinicIT
                </span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                For over two decades, we've been committed to providing exceptional healthcare services to our community with compassionate care.
              </p>
            </div>
            
            {/* CTA Button */}
            <div className="pt-6">
                <Link to="/booking">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                    <Calendar className="w-5 h-5 mr-2" />
                    Schedule Visit
                  </Button>
                </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Mission */}
            <Card className="card-hover" padding="lg">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
                  <p className="text-gray-600 leading-relaxed">
                    To provide exceptional, patient-centered healthcare that improves 
                    the health and well-being of individuals and families in our community. 
                    We are committed to delivering compassionate care with clinical excellence, 
                    innovation, and respect for every person we serve.
                  </p>
                </div>
              </div>
            </Card>

            {/* Vision */}
            <Card className="card-hover" padding="lg">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
                  <p className="text-gray-600 leading-relaxed">
                    To be the leading healthcare provider in our region, recognized for 
                    clinical excellence, innovative treatments, and exceptional patient 
                    experiences. We envision a healthier community where every individual 
                    has access to quality healthcare when they need it most.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These fundamental principles guide our actions and decisions in 
              providing the best possible healthcare experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center card-hover" padding="lg">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                  <value.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities & Technology Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              State-of-the-Art Facilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our modern medical center is equipped with the latest technology 
              and designed to provide a comfortable, healing environment.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {facilities.map((facility, index) => (
              <Card key={index} className="overflow-hidden card-hover" padding="none">
                <img
                  src={facility.image}
                  alt={facility.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{facility.title}</h3>
                  <p className="text-gray-600">{facility.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements & Recognition Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Awards & Recognition
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Our commitment to excellence has been recognized by leading healthcare 
                organizations and accreditation bodies.
              </p>
              
              <div className="space-y-4">
                {achievements.map((achievement, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {teamStats.map((stat, index) => (
                <Card key={index} className="text-center" padding="lg">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{stat.number}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced leadership team is dedicated to maintaining the highest 
              standards of medical care and patient satisfaction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockDoctors.map((doctor) => (
              <Card key={doctor.id} className="text-center card-hover" padding="lg">
                <img
                  src={doctor.photo}
                  alt={doctor.name}
                  className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
                />
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{doctor.name}</h3>
                <p className="text-blue-600 text-sm mb-3">{doctor.specialty}</p>
                <div className="space-y-1">
                  {doctor.qualifications.slice(0, 2).map((qual, index) => (
                    <p key={index} className="text-xs text-gray-600">{qual}</p>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/doctors">
              <Button size="lg" variant="outline">
                <Users className="w-5 h-5 mr-2" />
                View All Doctors
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920&h=1080&fit=crop&crop=center"
            alt="Modern Medical Center"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/80 via-blue-900/85 to-blue-800/80"></div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500 rounded-full opacity-10 blur-3xl transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 translate-y-32"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="space-y-6">
            {/* Section Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
                Experience the ClinicIT Difference
              </span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl mb-6">
                <Building className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                Join Thousands of{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  Satisfied Patients
                </span>
            </h2>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                Schedule your appointment today and discover what sets us apart in providing exceptional healthcare.
            </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Link to="/booking">
                <Button size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Appointment
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-slate-800 backdrop-blur-sm font-semibold px-8 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all duration-300">
                  <Heart className="w-5 h-5 mr-2" />
                  Learn About Our Services
                </Button>
              </Link>
            </div>

            {/* Additional Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-8 pt-8">
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm font-medium">20+ Years of Excellence</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                <span className="text-sm font-medium">Award-Winning Care</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm font-medium">Patient-Centered Approach</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;